/**
 * Admin vocabulary: `photos/config/available-tags.json`
 * - Facets: `people`, `places`, `pets`, `general`, `events`, `holidays`, `cameras` (string arrays on disk; camera is max one value)
 * - Legacy: flat `tags` + `groups` (see normalizeGroupsForPicker)
 */

/** UI order: Camera last (single-select). */
export const FACET_ROW_META = [
  { key: 'people', label: 'People', mode: 'multi' },
  { key: 'places', label: 'Places', mode: 'multi' },
  { key: 'pets', label: 'Pets', mode: 'multi' },
  { key: 'general', label: 'General', mode: 'multi' },
  { key: 'events', label: 'Events', mode: 'multi' },
  { key: 'holidays', label: 'Holidays', mode: 'multi' },
  { key: 'cameras', label: 'Camera', mode: 'single' },
];

export const FACET_KEYS = FACET_ROW_META.map((r) => r.key);

export function emptyFacetState() {
  return { people: [], places: [], pets: [], general: [], events: [], holidays: [], cameras: [] };
}

/**
 * Build facet picker state from photo metadata: use typed arrays (`people`, etc.) when present,
 * otherwise infer from legacy flat `tags` entries like "People - …" / "Camera - …".
 * Prevents empty editors on legacy photos and accidental clearing on save.
 */
export function hydrateFacetsFromLegacyTags(meta) {
  const raw = meta && typeof meta === 'object' ? meta : {};
  const tags = normalizeTagArray(raw.tags);
  const take = (displayPrefix, fieldKey) => {
    const fromField = normalizeStringList(raw[fieldKey]);
    if (fromField.length > 0) return fromField;
    const p = `${displayPrefix} - `;
    return tags
      .filter((t) => t.startsWith(p))
      .map((t) => t.slice(p.length).trim())
      .filter(Boolean);
  };
  return {
    people: [...take('People', 'people')],
    places: [...take('Places', 'places')],
    pets: [...take('Pets', 'pets')],
    general: [...take('General', 'general')],
    events: [...take('Events', 'events')],
    holidays: [...take('Holidays', 'holidays')],
    cameras: normalizeCameraList(take('Camera', 'cameras')),
  };
}

/** At most one camera value (metadata + API payloads use a string array of length 0–1). */
export function normalizeCameraList(val) {
  const arr = normalizeStringList(val);
  if (arr.length <= 1) return arr;
  return [arr[0]];
}

/** Coerce metadata-style string lists (same rules as App.js had inline). */
export function normalizeStringList(val) {
  if (Array.isArray(val)) {
    return val.map((s) => String(s).trim()).filter(Boolean);
  }
  if (val == null || val === '') return [];
  if (typeof val === 'string') {
    const t = val.trim();
    return t ? [t] : [];
  }
  return [];
}

export function normalizeTagArray(tags) {
  if (Array.isArray(tags)) {
    return tags.map((t) => String(t).trim()).filter(Boolean);
  }
  if (tags == null || tags === '') return [];
  if (typeof tags === 'string') {
    const s = tags.trim();
    return s ? [s] : [];
  }
  return [];
}

/** Canonical comparison key for facet values (handles whitespace/Unicode/case drift). */
function facetCompareKey(v) {
  return String(v ?? '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function sortUniqueStrings(arr) {
  return [...new Set(arr)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Ensure "Unknown" appears for place / camera pickers when absent (listed last). */
function withUnknownOption(sortedList) {
  const u = 'Unknown';
  if (sortedList.includes(u)) return sortedList;
  return [...sortedList, u];
}

function legacyNamesFromFlatTags(flatTags, displayPrefix) {
  const p = `${displayPrefix} - `;
  return flatTags
    .map((t) => String(t).trim())
    .filter((t) => t.startsWith(p))
    .map((t) => t.slice(p.length).trim())
    .filter(Boolean);
}

/**
 * Dropdown option lists for People / Places / Pets / General / Events / Holidays / Camera.
 * Uses explicit arrays on the config; if empty, derives labels from legacy flat `tags`
 * ("People - …", "General - …", "Camera - …", etc.) so older `available-tags.json` still works.
 * Places and cameras always include "Unknown".
 */
export function normalizeFacetOptions(data) {
  const flatTags = Array.isArray(data?.tags) ? data.tags.map((t) => String(t).trim()).filter(Boolean) : [];
  let people = sortUniqueStrings(normalizeStringList(data?.people));
  let places = sortUniqueStrings(normalizeStringList(data?.places));
  let pets = sortUniqueStrings(normalizeStringList(data?.pets));
  let general = sortUniqueStrings(normalizeStringList(data?.general));
  let events = sortUniqueStrings(normalizeStringList(data?.events));
  let holidays = sortUniqueStrings(normalizeStringList(data?.holidays));
  let cameras = sortUniqueStrings(normalizeStringList(data?.cameras));
  if (people.length === 0) people = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'People'));
  if (places.length === 0) places = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'Places'));
  if (pets.length === 0) pets = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'Pets'));
  if (general.length === 0) general = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'General'));
  if (events.length === 0) events = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'Events'));
  if (holidays.length === 0) holidays = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'Holidays'));
  if (cameras.length === 0) cameras = sortUniqueStrings(legacyNamesFromFlatTags(flatTags, 'Camera'));
  places = withUnknownOption(places);
  cameras = withUnknownOption(cameras);
  return { people, places, pets, general, events, holidays, cameras };
}

/**
 * Union config-derived facet options with every value present on loaded gallery metadata
 * (typed arrays + legacy flat `tags`). Fixes empty filter dropdowns when `available-tags.json`
 * omits names that were saved on photos.
 */
export function mergeFacetOptionsWithGalleryPhotos(baseOptions, photos) {
  if (!baseOptions || typeof baseOptions !== 'object') return baseOptions;
  if (!Array.isArray(photos) || photos.length === 0) return baseOptions;
  const out = { ...baseOptions };
  for (const { key } of FACET_ROW_META) {
    const set = new Set(normalizeStringList(out[key]));
    for (const p of photos) {
      const h = hydrateFacetsFromLegacyTags(p.metadata);
      const vals = key === 'cameras' ? normalizeStringList(h.cameras) : normalizeStringList(h[key]);
      vals.forEach((v) => set.add(v));
    }
    out[key] = sortUniqueStrings([...set]);
  }
  out.places = withUnknownOption(out.places);
  out.cameras = withUnknownOption(out.cameras);
  return out;
}

/**
 * Filter value starting with `!` means "must NOT have this attribute" (typed field or legacy tag).
 * Leading `!` only; value `!!Name` is treated as NOT `!Name` (unlikely for real names).
 */
export function facetFilterEntryLabel(raw) {
  const s = String(raw ?? '').trim();
  if (!s.startsWith('!')) return s;
  const inner = s.slice(1).trim();
  return inner ? `NOT ${inner}` : s;
}

/** If both `opt` and `!opt` are selected (e.g. native multiselect), keep the include and drop the negation. */
export function dedupeFacetIncludeExclude(selected, baseOptions) {
  const base = Array.isArray(baseOptions) ? baseOptions : [];
  const set = new Set(Array.isArray(selected) ? selected.map((x) => String(x)) : []);
  for (const opt of base) {
    if (set.has(opt) && set.has(`!${opt}`)) set.delete(`!${opt}`);
  }
  return [...set];
}

/** OR within each facet; AND across facets. Matches metadata fields or legacy "Prefix - name" tags.
 * Negated entries use a leading `!` on the value (e.g. `!Chuck Whitson` = photo must not tag that person). */
export function photoMatchesFacetFilters(metadata, filters) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const legacyTags = normalizeTagArray(meta.tags);

  const photoHasFacetName = (fieldKey, legacyPrefix, name) => {
    const n = String(name ?? '').trim();
    if (!n) return false;
    const key = facetCompareKey(n);
    const vals = new Set(normalizeStringList(meta[fieldKey]).map(facetCompareKey));
    if (vals.has(key)) return true;
    const p = `${legacyPrefix} - `;
    const legacyNorm = new Set(legacyTags.map(facetCompareKey));
    return legacyNorm.has(facetCompareKey(p + n));
  };

  const check = (selectedRaw, fieldKey, legacyPrefix) => {
    const selected = Array.isArray(selectedRaw)
      ? selectedRaw.map((s) => String(s).trim()).filter(Boolean)
      : [];
    if (!selected.length) return true;

    const positives = [];
    const negatives = [];
    for (const s of selected) {
      if (s.startsWith('!')) {
        const inner = s.slice(1).trim();
        if (inner) negatives.push(inner);
      } else {
        positives.push(s);
      }
    }

    const positiveOk =
      positives.length === 0 || positives.some((n) => photoHasFacetName(fieldKey, legacyPrefix, n));
    const negativeOk = negatives.every((n) => !photoHasFacetName(fieldKey, legacyPrefix, n));
    return positiveOk && negativeOk;
  };

  return (
    check(filters.people || [], 'people', 'People') &&
    check(filters.places || [], 'places', 'Places') &&
    check(filters.pets || [], 'pets', 'Pets') &&
    check(filters.general || [], 'general', 'General') &&
    check(filters.events || [], 'events', 'Events') &&
    check(filters.holidays || [], 'holidays', 'Holidays') &&
    check(filters.cameras || [], 'cameras', 'Camera')
  );
}

export function normalizeGroupId(name, explicitId) {
  if (explicitId != null && String(explicitId).trim()) {
    return String(explicitId)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-|-$/g, '') || 'group';
  }
  return (
    String(name || 'group')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'group'
  );
}

/** Canonical value written to photo metadata for a sub-tag under a real group. */
export function tagValue(group, sub) {
  const s = String(sub).trim();
  if (!s) return '';
  if (group.valuePrefix === '') return s;
  return `${group.id}/${s}`;
}

/**
 * Build UI groups from API payload. Legacy flat-only config becomes one synthetic group
 * (sub values stored on photos without a "groupId/" prefix).
 */
export function normalizeGroupsForPicker(data) {
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const withSubs = groups.filter(
    (g) => Array.isArray(g.subTags) && g.subTags.some((s) => String(s).trim())
  );
  if (withSubs.length > 0) {
    return withSubs.map((g) => ({
      id: normalizeGroupId(g.name, g.id),
      name: String(g.name || g.id || 'Categories').trim() || 'Categories',
      subTags: [
        ...new Set(
          (g.subTags || [])
            .map((s) => String(s).trim())
            .filter(Boolean)
        ),
      ],
      valuePrefix: null,
    }));
  }
  const flat = Array.isArray(data?.tags)
    ? [...new Set(data.tags.map((t) => String(t).trim()).filter(Boolean))]
    : [];
  if (flat.length === 0) return [];
  return [
    {
      id: 'legacy',
      name: 'Categories',
      subTags: flat,
      valuePrefix: '',
    },
  ];
}

/** Display string for a tag on thumbnails / chips. */
export function displayTagLabel(tag, groups) {
  const t = String(tag);
  const i = t.indexOf('/');
  if (i <= 0) return t;
  const gid = t.slice(0, i);
  const sub = t.slice(i + 1);
  const g = Array.isArray(groups) ? groups.find((x) => x.id === gid) : null;
  if (g) return `${g.name} › ${sub}`;
  // Flat config values like "Camera/Nikon" (category / label) without a real group id
  return `${gid} › ${sub}`;
}

/**
 * All tag strings allowed on photos — mirrors `getAllowedTagsFromConfigData` in functions.
 * Prefer flat `tags` when non-empty; otherwise canonical `groupId/sub` from `groups`.
 */
export function allAdminTagValuesForConfig(data) {
  const flat = normalizeAdminTagsList(data);
  if (flat.length > 0) return flat;
  const opts = flattenTagFilterOptions(normalizeGroupsForPicker(data));
  return [...new Set(opts.map((o) => o.value))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/** Sorted unique admin tags from API payload (`tags` array only). */
export function normalizeAdminTagsList(data) {
  const raw = Array.isArray(data?.tags) ? data.tags : [];
  return [...new Set(raw.map((t) => String(t).trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/** Flat pill options for gallery filters (no expand/collapse). */
export function flattenTagFilterOptions(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return [];
  const rows = [];
  for (const g of groups) {
    const subs = Array.isArray(g.subTags) ? g.subTags : [];
    for (const sub of subs) {
      const val = tagValue(g, sub);
      if (!val) continue;
      rows.push({
        value: val,
        label: displayTagLabel(val, groups),
      });
    }
  }
  const seen = new Set();
  const unique = rows.filter((r) => {
    if (seen.has(r.value)) return false;
    seen.add(r.value);
    return true;
  });
  unique.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
  return unique;
}
